import { ThemeEvents } from '@theme/events';

const AB_CLASS = 'ig-sticky-atc';

/**
 * Lightweight sticky ATC bar for PDP A/B tests.
 * Visibility is gated by Intelligems class or permanent setting.
 * Clicks through to the main product form ATC so selling plan / gifts stay in sync.
 */
class PdpStickyAtcAb extends HTMLElement {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {HTMLButtonElement | null} */
  #button = null;

  /** @type {HTMLImageElement | null} */
  #thumb = null;

  /** @type {HTMLElement | null} */
  #name = null;

  /** @type {boolean} */
  #mainAtcOutOfView = false;

  connectedCallback() {
    this.#button = this.querySelector('[data-pdp-sticky-atc-button]');
    this.#thumb = this.querySelector('[data-pdp-sticky-atc-thumb]');
    this.#name = this.querySelector('[data-pdp-sticky-atc-name]');

    this.#button?.addEventListener('click', this.#handleClick);
    document.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#observeAbClass();
    this.#setupIntersectionObserver();
    this.#syncFromMainAtc();
    this.#updateVisibility();
  }

  disconnectedCallback() {
    this.#button?.removeEventListener('click', this.#handleClick);
    document.removeEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate);
    this.#observer?.disconnect();
  }

  get #isExperimentActive() {
    if (this.hasAttribute('data-show-permanently')) return true;

    return (
      document.documentElement.classList.contains(AB_CLASS) ||
      document.body.classList.contains(AB_CLASS)
    );
  }

  #observeAbClass = () => {
    const observer = new MutationObserver(() => {
      this.#updateVisibility();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class'],
    });

    if (document.body) {
      observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class'],
      });
    }
  };

  #getMainAtcButton = () => {
    const candidates = document.querySelectorAll('product-form-component button[name="add"]');

    for (const button of candidates) {
      if (!(button instanceof HTMLButtonElement)) continue;
      if (button.closest('.quick-view-one-time-variant')) continue;
      if (button.closest('pdp-sticky-atc-ab')) continue;
      return button;
    }

    return null;
  };

  #getObserveTarget = () => {
    const mainAtc = this.#getMainAtcButton();
    if (!mainAtc) return null;

    return (
      mainAtc.closest('.buy-buttons-block') ||
      mainAtc.closest('product-form-component') ||
      mainAtc
    );
  };

  #setupIntersectionObserver = () => {
    const target = this.#getObserveTarget();
    if (!target) return;

    this.#observer?.disconnect();

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const viewportTop = entry.rootBounds?.top ?? 0;

          // Only count the main ATC as passed when it leaves upwards.
          // On load it usually sits below the fold, which must stay hidden.
          this.#mainAtcOutOfView = !entry.isIntersecting && entry.boundingClientRect.bottom <= viewportTop;
          this.#updateVisibility();
        }
      },
      {
        root: null,
        threshold: 0,
        rootMargin: '0px 0px 0px 0px',
      }
    );

    this.#observer.observe(target);
  };

  #updateVisibility = () => {
    const shouldShow = this.#isExperimentActive && this.#mainAtcOutOfView;
    this.classList.toggle('is-visible', shouldShow);
    this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
  };

  #syncFromMainAtc = () => {
    const mainAtc = this.#getMainAtcButton();
    if (!mainAtc || !this.#button) return;

    const isDisabled =
      mainAtc.disabled ||
      mainAtc.hasAttribute('disabled') ||
      mainAtc.classList.contains('disabled');

    this.#button.disabled = isDisabled;
    this.#button.setAttribute('aria-disabled', isDisabled ? 'true' : 'false');

    if (isDisabled) {
      this.#button.textContent = 'Sold Out';
    } else {
      this.#button.textContent = this.dataset.buttonText || 'Add to Cart';
    }

    const media = mainAtc.closest('add-to-cart-component')?.dataset.productVariantMedia;
    if (media && this.#thumb) {
      this.#thumb.src = media;
    }
  };

  /**
   * @param {Event} event
   */
  #handleVariantUpdate = (event) => {
    const detail = /** @type {{ detail?: { resource?: { featured_media?: { preview_image?: { src?: string } } }, available?: boolean }, data?: { productId?: string } } }} */ (
      event
    ).detail;

    const productId = this.dataset.productId;
    if (productId && detail?.data?.productId && detail.data.productId !== productId) {
      return;
    }

    const imageSrc = detail?.resource?.featured_media?.preview_image?.src;
    if (imageSrc && this.#thumb) {
      this.#thumb.src = imageSrc.includes('?')
        ? `${imageSrc}&width=88`
        : `${imageSrc}?width=88`;
    }

    this.#syncFromMainAtc();

    // Main ATC may remorph after variant update — rebind observer.
    requestAnimationFrame(() => {
      this.#setupIntersectionObserver();
      this.#syncFromMainAtc();
    });
  };

  #handleClick = (event) => {
    event.preventDefault();

    const mainAtc = this.#getMainAtcButton();
    if (!mainAtc || mainAtc.disabled) return;

    mainAtc.click();
  };
}

if (!customElements.get('pdp-sticky-atc-ab')) {
  customElements.define('pdp-sticky-atc-ab', PdpStickyAtcAb);
}
